import React, { useEffect, useRef, useState } from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

import { DUR_ENTRANCE, EASE_ENTRANCE } from '../chat/uiTokens';

import { formatStepSeconds } from './WorkSummary';
import type { WorkRow } from './projectTurn';

const TICK_MS = 100;
const ICON_SIZE = 14;
const TABULAR = 'tabular-nums';
const BETWEEN_STEPS_STATUS = 'Working on it';

interface LiveWorkProps {
  rows: ReadonlyArray<WorkRow>;
  statusText: string;
  active: boolean;
}

function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

function useFirstSeen(key: string): number {
  const seen = useRef(new Map<string, number>());
  if (!seen.current.has(key)) {
    seen.current.set(key, Date.now());
  }
  return seen.current.get(key) ?? Date.now();
}

function FinishedRow({ row }: { row: WorkRow }) {
  return (
    <Stack
      component="li"
      direction="row"
      spacing={1}
      alignItems="center"
      data-testid="live-work-step"
      data-state={row.state}
      sx={{ minHeight: 24 }}
    >
      {row.state === 'running' ? (
        <StopCircleOutlinedIcon sx={{ fontSize: ICON_SIZE, color: 'text.disabled', flex: 'none' }} />
      ) : row.state === 'error' ? (
        <ErrorOutlineRoundedIcon sx={{ fontSize: ICON_SIZE, color: 'error.main', flex: 'none' }} />
      ) : (
        <CheckCircleOutlineIcon sx={{ fontSize: ICON_SIZE, color: 'success.main', flex: 'none' }} />
      )}
      <Typography
        data-testid="live-work-step-label"
        sx={{ fontSize: 13, lineHeight: 1.5, color: 'text.secondary', minWidth: 0, overflowWrap: 'anywhere' }}
      >
        {row.label}
      </Typography>
    </Stack>
  );
}

function CurrentRow({ text, startedAt }: { text: string; startedAt: number }) {
  const now = useNow();
  return (
    <Stack
      component="li"
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        minHeight: 24,
        animation: `askRichardStatusIn ${DUR_ENTRANCE}ms ${EASE_ENTRANCE}`,
        '@keyframes askRichardStatusIn': {
          from: { opacity: 0, transform: 'translateY(4px)' },
          to: { opacity: 1, transform: 'none' },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <CircularProgress
        size={ICON_SIZE}
        thickness={5}
        aria-hidden
        sx={{ flex: 'none', '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}
      />
      <Typography
        data-testid="ui-stream-status-text"
        sx={(theme) => ({
          fontSize: 13,
          lineHeight: 1.5,
          minWidth: 0,
          overflowWrap: 'anywhere',
          fontWeight: 500,
          background: `linear-gradient(90deg, ${theme.palette.text.secondary} 30%, ${theme.palette.text.primary} 50%, ${theme.palette.text.secondary} 70%)`,
          backgroundSize: '200% 100%',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          animation: 'askRichardStatusShimmer 2.4s linear infinite',
          '@keyframes askRichardStatusShimmer': {
            '0%': { backgroundPosition: '200% 0' },
            '100%': { backgroundPosition: '-200% 0' },
          },
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            background: 'none',
            WebkitTextFillColor: theme.palette.text.secondary,
          },
        })}
      >
        {text}
      </Typography>
      <Typography
        data-testid="ui-stream-status-timer"
        sx={{ fontSize: 12, color: 'text.disabled', fontVariantNumeric: TABULAR, flex: 'none', whiteSpace: 'nowrap' }}
      >
        {formatStepSeconds(Math.max(0, now - startedAt))}
      </Typography>
    </Stack>
  );
}

export default function LiveWork({ rows, statusText, active }: LiveWorkProps) {
  const running = rows[rows.length - 1]?.state === 'running';
  const finished = active && running ? rows.slice(0, -1) : rows;
  const current = !running && statusText === finished[finished.length - 1]?.label ? BETWEEN_STEPS_STATUS : statusText;
  const startedAt = useFirstSeen(`${finished.length}:${running}:${current}`);
  return (
    <Box
      component="ol"
      data-testid={active ? 'ui-stream-status' : 'ui-stream-activity'}
      role={active ? 'status' : undefined}
      aria-live={active ? 'polite' : 'off'}
      sx={{ listStyle: 'none', m: 0, p: 0, py: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}
    >
      {finished.map((row) => (
        <FinishedRow key={row.id} row={row} />
      ))}
      {active ? <CurrentRow text={current} startedAt={startedAt} /> : null}
    </Box>
  );
}
